import { z } from 'zod';

import {
  timeEntryStatusValues,
  type ClockInRequest,
  type ClockInResponse,
  type ClockOutRequest,
  type ClockOutResponse,
  type GetSelfTimeEntryStatusRequest,
  type GetSelfTimeEntryStatusResponse,
  type SelfClockInRequest,
  type SelfClockOutRequest,
  type AdjustTimeEntryRequest,
  type AdjustTimeEntryResponse,
  type ListTimeEntriesRequest,
  type ListTimeEntriesResponse,
  type TimeEntryDto,
  type TimeEntryStatus,
} from './time-tracking.dto.js';

const timestampSchema = z.iso.datetime();

export const TimeEntryStatusSchema: z.ZodType<TimeEntryStatus> = z.enum(timeEntryStatusValues);

export const TimeEntryDtoSchema: z.ZodType<TimeEntryDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid().nullable(),
  locationId: z.uuid().nullable(),
  stationId: z.uuid().nullable(),
  employeeId: z.uuid(),
  status: TimeEntryStatusSchema,
  clockInAt: timestampSchema,
  clockOutAt: timestampSchema.nullable(),
  totalMinutes: z.number().int().nonnegative().nullable(),
  isAdjusted: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const ClockInRequestSchema: z.ZodType<ClockInRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  stationId: z.uuid().optional(),
});

export const ClockInResponseSchema: z.ZodType<ClockInResponse> = z.object({
  timeEntry: TimeEntryDtoSchema,
});

export const ClockOutRequestSchema: z.ZodType<ClockOutRequest> = z.object({
  companyId: z.uuid(),
  timeEntryId: z.uuid(),
});

export const ClockOutResponseSchema: z.ZodType<ClockOutResponse> = z.object({
  timeEntry: TimeEntryDtoSchema,
});

export const SelfClockInRequestSchema: z.ZodType<SelfClockInRequest> = z.object({
  companyId: z.uuid(),
});

export const SelfClockOutRequestSchema: z.ZodType<SelfClockOutRequest> = z.object({
  companyId: z.uuid(),
});

export const GetSelfTimeEntryStatusRequestSchema: z.ZodType<GetSelfTimeEntryStatusRequest> = z.object({
  companyId: z.uuid(),
});

export const GetSelfTimeEntryStatusResponseSchema: z.ZodType<GetSelfTimeEntryStatusResponse> = z
  .object({
    openTimeEntry: TimeEntryDtoSchema.nullable(),
  })
  .strict();

export const ListTimeEntriesRequestSchema: z.ZodType<ListTimeEntriesRequest> = z.object({
  companyId: z.uuid(),
  employeeId: z.uuid().optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
});

export const ListTimeEntriesResponseSchema: z.ZodType<ListTimeEntriesResponse> = z.object({
  items: z.array(TimeEntryDtoSchema),
});

export const AdjustTimeEntryRequestSchema: z.ZodType<AdjustTimeEntryRequest> = z.object({
  companyId: z.uuid(),
  timeEntryId: z.uuid(),
  newClockInAt: timestampSchema,
  newClockOutAt: timestampSchema.nullable(),
  reason: z.string().min(1).max(500),
});

export const AdjustTimeEntryResponseSchema: z.ZodType<AdjustTimeEntryResponse> = z.object({
  timeEntry: TimeEntryDtoSchema,
});
